// Computed status colours for the Workbench design: pipeline stage ramp and lead-quality tones.
// Values come from docs/superpowers/specs/2026-10-22-workbench-redesign/Colors.dc.html.
import type { LeadStatus } from '@/lib/callAnalysis'

export type StageType = 'open' | 'won' | 'lost'

/** Curated blue ramp for open stages, lightest (earliest) to darkest (latest). */
export const STAGE_RAMP: readonly string[] = [
  '#BFDBFE',
  '#93C5FD',
  '#60A5FA',
  '#3B82F6',
  '#2563EB',
  '#1E40AF',
]
export const STAGE_WON = '#15803D'
export const STAGE_LOST = '#A8A29E'

const RAMP_START = [0xbf, 0xdb, 0xfe]
const RAMP_END = [0x1e, 0x40, 0xaf]

function rampPosition(openIndex: number, openCount: number): number {
  return Math.min(Math.max(openIndex, 0), openCount - 1)
}

function interpolate(openIndex: number, openCount: number): string {
  const t = openIndex / (openCount - 1)
  const channels = RAMP_START.map((from, k) => {
    const to = RAMP_END[k] as number
    return Math.round(from + (to - from) * t)
  })
  return '#' + channels.map((c) => c.toString(16).padStart(2, '0')).join('').toUpperCase()
}

/**
 * Stage colour from position, not a picker. Won and lost have fixed colours; open stages walk
 * the blue ramp by their rank among the pipeline's open stages (a missing type counts as open).
 */
export function stageColor(type: StageType | undefined, openIndex: number, openCount: number): string {
  if (type === 'won') return STAGE_WON
  if (type === 'lost') return STAGE_LOST
  if (openCount <= 1) return STAGE_RAMP[0] as string
  const index = rampPosition(openIndex, openCount)
  if (openCount <= STAGE_RAMP.length) {
    return STAGE_RAMP[Math.round((index * (STAGE_RAMP.length - 1)) / (openCount - 1))] as string
  }
  return interpolate(index, openCount)
}

/** Colour for every stage of a pipeline, keyed by stage `_id`. Open stages are ranked by `order`. */
export function stageColorMap(
  stages: { _id: string; order: number; type?: StageType }[],
): Record<string, string> {
  const open = stages
    .filter((s) => s.type !== 'won' && s.type !== 'lost')
    .sort((a, b) => a.order - b.order)
  const rank = new Map(open.map((s, i) => [s._id, i]))
  const map: Record<string, string> = {}
  for (const s of stages) {
    map[s._id] = stageColor(s.type, rank.get(s._id) ?? 0, open.length)
  }
  return map
}

/** Lead-quality chip tones: bg/fg for the chip, bar for stacked bars, ring for the outline-only state. */
export const LEAD_QUALITY_TONE: Record<LeadStatus, { bg: string; fg: string; bar: string; ring: string }> = {
  qualified: { bg: '#E8F5EE', fg: '#166534', bar: '#15803D', ring: 'transparent' },
  nurture: { bg: '#FEF3C7', fg: '#92400E', bar: '#F59E0B', ring: 'transparent' },
  // "Needs another call": violet is reserved for this so it never reads as a pipeline stage.
  in_progress: { bg: '#F3E8FF', fg: '#6B21A8', bar: '#7C3AED', ring: 'transparent' },
  disqualified: { bg: '#F3F1ED', fg: '#5B5852', bar: '#A8A29E', ring: 'transparent' },
  unscored: { bg: '#FFFFFF', fg: '#5B5852', bar: '#DDD9D1', ring: '#DDD9D1' },
}
